// Предикат «этот запрос адресован нашему API».
//
// Нужен потому, что глобальным axios ходят не только в rentalman-api: тем же
// инстансом уходят запросы на api.green-api.com, gprs.autofon.ru, id.starline.ru
// и на presigned URL нашего бакета в storage.yandexcloud.net. Всё, что вешается
// на глобальный инстанс (наш JWT, anti-scrape client_token, версия приложения),
// без такого предиката уезжает и туда.
//
// Политика — allowlist по origin: адресат наш, только если его origin точно
// совпал с одним из известных. Сравнение по вхождению подстроки здесь было бы
// дырой: `https://rentprog.net.evil.example` содержит наш хост целиком, а
// `https://rentprog.net@evil.example` ведёт на evil.example, хотя начинается с
// нашего имени. Разбор URL отдан браузерному `URL`, который нормализует
// регистр хоста, дефолтный порт, обратные слэши и userinfo одинаково с тем, как
// потом резолвит адрес сам браузер.

// Origin в нормализованной форме (`https://host[:port]`) или null, если разобрать
// не удалось. Null означает «не доказано, что наш» — не «наш по умолчанию».
//
// Никакой самодельной нормализации входа здесь нет намеренно: ведущие пробелы,
// управляющие символы и `\t\n\r` внутри строки убирает сам URL-парсер, и
// повторять это руками — значит держать второй канон правил разбора, который
// разъедется с первым. Проверено мутацией: своя нормализация не меняла ни одного
// исхода. Единственное, что делается до разбора, — пустая строка отсекается,
// чтобы пустой `url` честно отдал разбор базе, а не притворился адресом.
export function originOf(url, base) {
  const value = String(url == null ? "" : url).trim();
  if (!value) return null;

  try {
    // Протокол-относительный `//host/path` наследует схему базы — так же, как
    // это сделает браузер. `new URL(value, base)` делает это сам, когда база
    // есть; ветка нужна только на случай отсутствующей базы.
    const parsed =
      value.slice(0, 2) === "//" && !base
        ? new URL("https:" + value)
        : new URL(value, base || undefined);
    // Non-special схемы (`blob:`, `capacitor:`, `data:`) дают origin строкой
    // "null" — сравнивать их между собой как равные нельзя.
    return parsed.origin && parsed.origin !== "null" ? parsed.origin : null;
  } catch (e) {
    return null;
  }
}

// Куда запрос уйдёт на самом деле. Повторяет правило axios `buildFullPath`:
// абсолютный `url` перебивает `baseURL`, относительный — резолвится через него,
// а без базы адресом становится origin самой страницы.
//
// `url` разбирается ВСЕГДА, а не только когда он абсолютный по регулярке.
// Регулярка смотрит на сырую строку — как и `isAbsoluteURL` самого axios, — но
// браузер перед разбором срезает ведущие пробелы и вычищает `\t\n\r`, поэтому
// строка вида `" //evil.example"` регуляркой абсолютной не считается, а уходит
// всё равно на evil.example. Ревью показало семь таких расхождений; ни одно
// сегодня не достижимо (базы пустой не бывает), но держать предикат и транспорт
// на разных правилах — значит ждать, пока call-site появится.
export function requestTargetOrigin(config, pageOrigin) {
  const url = config && config.url;
  const base = (config && config.baseURL) || pageOrigin;

  return originOf(url, base) || originOf(base, pageOrigin);
}

// options: { bases: string[], pageOrigin: string }
//
// В bases идут все базы, которые в этой сессии могут оказаться нашим API:
// действующая `axios.defaults.baseURL`, региональная из стора, сборочная
// VUE_APP_API_URL и DEFAULT_API_BASE. Список именно такой ширины намеренно —
// ложный «чужой» здесь дороже ложного «свой»: запрос к нашему API без
// Authorization даёт 401 и разлогин, а без X-Client-Token — 403 Rack::Attack и
// рекавери-петлю (инцидент 2026-08-24, tests/unit/antiScrapeRetryLoop.spec.js).
export function isOurApiRequest(config, options) {
  // Нет конфига — нет доказательства, что адресат наш. Политика файла одна и та
  // же для всех неизвестных: null значит «не доказано», а не «наш по умолчанию».
  if (!config) return false;

  const opts = options || {};
  const pageOrigin = opts.pageOrigin || null;

  const target = requestTargetOrigin(config, pageOrigin);
  if (!target) return false;

  const known = [];
  const remember = (value) => {
    const origin = originOf(value, pageOrigin);
    if (origin && known.indexOf(origin) === -1) known.push(origin);
  };

  (opts.bases || []).forEach(remember);
  // Origin самой страницы — не третья сторона: туда отдан сам бандл.
  remember(pageOrigin);

  return known.indexOf(target) !== -1;
}
